import React, { useEffect, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, LineChart, Line, XAxis, YAxis, CartesianGrid, Legend } from "recharts";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import moment from "moment";
import axios from "axios";
import { Activity, PieChart as PieChartIcon, CheckCircle, AlertTriangle, Globe, ChevronDown, ChevronRight, TrendingUp, TrendingDown, Minus, Info, Filter } from 'lucide-react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt } from "@fortawesome/free-solid-svg-icons";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Dashboard.css";
import { API_URL } from "../config.js";
import CanvasJSReact from "@canvasjs/react-charts";

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

const LEAD_COLORS = [
    "hsl(217, 91%, 60%)",
    "hsl(160, 84%, 39%)",
    "hsl(280, 65%, 60%)",
    "hsl(160, 84%, 50%)",
    "hsl(38, 92%, 50%)",
    "hsl(0, 84%, 60%)",
    "hsl(0, 0%, 40%)",
];

const LeadSourceTable = ({ startDate, endDate, loading: parentLoading }) => {
    const [sources, setSources] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openSources, setOpenSources] = useState([]);
    const [utmDetails, setUtmDetails] = useState({});
    const [loadingUTM, setLoadingUTM] = useState({});

    const fetchSources = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("authToken");
            const url = `${API_URL}executive-report/lead-source-performance?startDate=${startDate.format("YYYY-MM-DD")}&endDate=${endDate.format("YYYY-MM-DD")}`;
            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}`, Accept: "*/*" },
            });
            if (response.data && response.data.success) {
                setSources(response.data.data.sources || []);
            }
        } catch (err) {
            console.error("Failed to fetch lead sources", err);
        } finally {
            setLoading(false);
        }
    };

    const fetchUTMDetails = async (sourceName) => {
        if (utmDetails[sourceName]) return; // Already fetched

        try {
            setLoadingUTM(prev => ({ ...prev, [sourceName]: true }));
            const token = localStorage.getItem("authToken");
            const url = `${API_URL}executive-report/utm-campaign-performance?startDate=${startDate.format("YYYY-MM-DD")}&endDate=${endDate.format("YYYY-MM-DD")}&utm_source=${sourceName}`;
            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}`, Accept: "*/*" },
            });
            if (response.data && response.data.success) {
                setUtmDetails(prev => ({ ...prev, [sourceName]: response.data.data.campaigns || [] }));
            }
        } catch (err) {
            console.error(`Failed to fetch UTM details for ${sourceName}`, err);
        } finally {
            setLoadingUTM(prev => ({ ...prev, [sourceName]: false }));
        }
    };

    useEffect(() => {
        fetchSources();
        setOpenSources([]);
        setUtmDetails({});
    }, [startDate, endDate]);

    const toggleSource = (sourceName) => {
        if (openSources.includes(sourceName)) {
            setOpenSources(openSources.filter(s => s !== sourceName));
        } else {
            setOpenSources([...openSources, sourceName]);
            fetchUTMDetails(sourceName);
        }
    };

    const getQualityIcon = (percentage) => {
        if (percentage >= 60) return <TrendingUp className="text-success" size={16} />;
        if (percentage >= 30) return <Minus className="text-warning" size={16} />;
        return <TrendingDown className="text-danger" size={16} />;
    };

    if (loading || parentLoading) {
        return (
            <div className="panel panel-default with-rounded-corner with-shadow mb-5">
                <div className="panel-body p-5 text-center">
                    <div className="spinner-border text-accent" role="status" style={{ color: '#4c5667' }}>
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Analyzing lead sources...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="panel panel-default with-rounded-corner with-shadow mb-5">
            <div className="panel-heading">
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                        <Globe className="text-dark" size={18} />
                        <h4 className="mb-0 fw-bold text-dark" style={{ fontSize: '1.1rem' }}>Lead Sources Analysis</h4>
                    </div>
                    <div className="d-flex align-items-center gap-3 bg-light bg-opacity-50 px-3 py-1 rounded-pill">
                        <Info size={14} className="text-primary" />
                        <div className="d-flex gap-3 small fw-medium text-muted" style={{ fontSize: '0.75rem' }}>
                            <span>
                                <span className="text-success fw-bold">&gt;60%</span>{" "}
                                <span className="text-dark">Good</span>
                            </span>
                            <span>
                                <span className="text-warning fw-bold">30-60%</span>{" "}
                                <span className="text-dark">Average</span>
                            </span>
                            <span>
                                <span className="text-danger fw-bold">&lt;30%</span>{" "}
                                <span className="text-dark">Poor</span>
                            </span>
                        </div>
                    </div>

                </div>
            </div>
            <div className="panel-body p-0">
                <div className="table-responsive">
                    <table className="table border-0 mb-0">
                        <thead>
                            <tr className="bg-light bg-opacity-50">
                                <th className="px-4 py-3 border-0 text-muted small fw-bold text-uppercase w-50px"></th>
                                <th className="px-4 py-3 border-0 text-muted small fw-bold text-uppercase">Lead Source</th>
                                <th className="px-4 py-3 border-0 text-muted small fw-bold text-uppercase text-end">Total Leads</th>
                                <th className="px-4 py-3 border-0 text-muted small fw-bold text-uppercase text-end">Healthy Leads</th>
                                <th className="px-4 py-3 border-0 text-muted small fw-bold text-uppercase text-end">Junk Leads</th>
                                <th className="px-4 py-3 border-0 text-muted small fw-bold text-uppercase">Quality Check</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sources.map((source, index) => {
                                const isOpen = openSources.includes(source.lead_source);
                                return (
                                    <React.Fragment key={source.lead_source}>
                                        <tr
                                            className={`align-middle border-bottom border-light cursor-pointer transition-all ${index % 2 === 0 ? 'bg-light bg-opacity-10' : ''}`}
                                            onClick={() => toggleSource(source.lead_source)}
                                            style={{ transition: 'background-color 0.2s' }}
                                        >
                                            <td className="px-4 py-3 text-center">
                                                {isOpen ? <ChevronDown size={16} className="text-muted" /> : <ChevronRight size={16} className="text-muted" />}
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`fw-bold ${isOpen ? 'text-primary' : 'text-dark'}`}>{source.lead_source}</span>
                                            </td>
                                            <td className="px-4 py-3 text-end fw-bold text-dark">{source.total_leads.toLocaleString()}</td>
                                            <td className="px-4 py-3 text-end text-success fw-medium">
                                                <div className="fw-bold fs-6">
                                                    {source.healthy_leads.total.toLocaleString()}
                                                </div>
                                                <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                                                    FU:<span className="fw-bold text-black">{source.healthy_leads.follow_up}</span> ·
                                                    PR:<span className="fw-bold text-black">{source.healthy_leads.prospect}</span> ·
                                                    SUB:<span className="fw-bold text-black">{source.healthy_leads.subscribed}</span>
                                                </div>
                                            </td>

                                            <td className="px-4 py-3 text-end text-danger fw-medium">
                                                <div className="fw-bold fs-6">
                                                    {source.junk_leads.total.toLocaleString()}
                                                </div>
                                                <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                                                    NR:<span className="fw-bold text-black">{source.junk_leads.no_response}</span> ·
                                                    NI:<span className="fw-bold text-black">{source.junk_leads.not_interested}</span> ·
                                                    JK:<span className="fw-bold text-black">{source.junk_leads.junk}</span>
                                                </div>
                                            </td>

                                            <td className="px-4 py-3">
                                                <div className="d-flex align-items-center gap-2">
                                                    {getQualityIcon(source.healthy_percentage)}
                                                    <span style={{ color: source.quality_color, fontSize: '0.85rem' }} className="fw-medium">
                                                        {source.quality_check}
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>
                                        {isOpen && (
                                            <tr>
                                                <td colSpan="6" className="p-0 border-0">
                                                    <div className="bg-light bg-opacity-30 p-4 border-bottom border-light">
                                                        {loadingUTM[source.lead_source] ? (
                                                            <div className="text-center py-3">
                                                                <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                                                                <span className="ms-2 small text-muted">Fetching campaign details...</span>
                                                            </div>
                                                        ) : (
                                                            <div className="table-responsive rounded-3 border border-light shadow-sm">
                                                                <table className="table table-sm mb-0 bg-white">
                                                                    <thead className="bg-light bg-opacity-50">
                                                                        <tr>
                                                                            <th className="px-3 py-2 border-0 text-muted smaller fw-bold text-uppercase">UTM Source</th>
                                                                            <th className="px-3 py-2 border-0 text-muted smaller fw-bold text-uppercase">Medium</th>
                                                                            <th className="px-3 py-2 border-0 text-muted smaller fw-bold text-uppercase">Campaign</th>
                                                                            <th className="px-3 py-2 border-0 text-muted smaller fw-bold text-uppercase text-end">Total</th>
                                                                            <th className="px-3 py-2 border-0 text-muted smaller fw-bold text-uppercase text-end text-success">Healthy</th>
                                                                            <th className="px-3 py-2 border-0 text-muted smaller fw-bold text-uppercase text-end text-danger">Junk</th>
                                                                            <th className="px-3 py-2 border-0 text-muted smaller fw-bold text-uppercase text-end">Quality</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {(utmDetails[source.lead_source] || []).length > 0 ? (
                                                                            utmDetails[source.lead_source].map((utm, uIdx) => (
                                                                                <tr key={uIdx} className="align-middle border-top border-light">
                                                                                    <td className="px-3 py-2 text-muted smaller">{utm.utm_source || "---"}</td>
                                                                                    <td className="px-3 py-2 text-muted smaller">{utm.utm_medium || "---"}</td>
                                                                                    <td className="px-3 py-2 fw-medium text-dark smaller">{utm.utm_campaign || "Untitled Campaign"}</td>
                                                                                    <td className="px-3 py-2 text-end fw-bold smaller">{utm.total_leads.toLocaleString()}</td>
                                                                                    <td className="px-3 py-2 text-end text-success smaller fw-medium">
                                                                                        <div className="fw-bold mb-1">
                                                                                            {utm.healthy_leads.total.toLocaleString()}
                                                                                        </div>
                                                                                        <div className="text-muted opacity-75" style={{ fontSize: '0.75rem' }}>
                                                                                            FU:<span className="fw-bold text-black">{utm.healthy_leads.follow_up}</span> |
                                                                                            PR:<span className="fw-bold text-black">{utm.healthy_leads.prospect}</span> |
                                                                                            SUB:<span className="fw-bold text-black">{utm.healthy_leads.subscribed}</span>
                                                                                        </div>
                                                                                    </td>

                                                                                    <td className="px-3 py-2 text-end text-danger smaller fw-medium">
                                                                                        <div className="fw-bold mb-1">
                                                                                            {utm.junk_leads.total.toLocaleString()}
                                                                                        </div>
                                                                                        <div className="text-muted opacity-75" style={{ fontSize: '0.65rem' }}>
                                                                                            NR:<span className="fw-bold text-black">{utm.junk_leads.no_response}</span> |
                                                                                            NI:<span className="fw-bold text-black">{utm.junk_leads.not_interested}</span> |
                                                                                            JK:<span className="fw-bold text-black">{utm.junk_leads.junk}</span>
                                                                                        </div>
                                                                                    </td>

                                                                                    <td className="px-3 py-2 text-end smaller" style={{ color: utm.quality_color }}>{utm.quality_check}</td>
                                                                                </tr>
                                                                            ))
                                                                        ) : (
                                                                            <tr><td colSpan="7" className="text-center py-3 text-muted small">No UTM campaigns found for this source</td></tr>
                                                                        )}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

const LeadStatusTable = ({ dispositionData, loading }) => {
    const leadStatuses = dispositionData?.disposition || [];
    const totalLeads = dispositionData?.total_leads || 0;
    const totalPercentage = dispositionData?.total_percentage ?? 100;

    const isHealthy = (description) => {
        const desc = (description || '').toLowerCase();
        return desc.includes('good') || desc.includes('active') || desc.includes('pipeline') || desc.includes('initial') || desc.includes('updated');
    };

    if (loading) {
        return (
            <div className="panel panel-default with-rounded-corner with-shadow mb-5">
                <div className="panel-body p-5 text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Loading table data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="panel panel-default with-rounded-corner with-shadow mb-5">
            <div className="panel-heading">
                <div className="d-flex align-items-center gap-2">
                    <PieChartIcon className="text-primary" size={18} />
                    <h4 className="mb-0 fw-bold text-dark" style={{ fontSize: '1.1rem' }}>All Lead Statuses Breakdown (Team-Wide)</h4>
                </div>
            </div>
            <div className="panel-body p-0">
                <div className="table-responsive">
                    <table className="table table-hover mb-0">
                        <thead>
                            <tr className="bg-light bg-opacity-50">
                                <th className="px-4 py-3 border-0 text-muted small fw-bold text-uppercase">Status</th>
                                <th className="px-4 py-3 border-0 text-muted small fw-bold text-uppercase text-end">Count</th>
                                <th className="px-4 py-3 border-0 text-muted small fw-bold text-uppercase text-end">% of Total</th>
                                <th className="px-4 py-3 border-0 text-muted small fw-bold text-uppercase">Health Check</th>
                            </tr>
                        </thead>
                        <tbody>
                            {leadStatuses.map((item, index) => {
                                const healthy = isHealthy(item.description);
                                return (
                                    <tr key={item.status} className="align-middle border-bottom border-light">
                                        <td className="px-4 py-3">
                                            <span className="fw-semibold text-dark">{item.status}</span>
                                        </td>
                                        <td className="px-4 py-3 text-end fw-bold text-dark">{item.count.toLocaleString()}</td>
                                        <td className="px-4 py-3 text-end text-muted">{item.percentage}%</td>
                                        <td className="px-4 py-3">
                                            <div className="d-flex align-items-center gap-2">
                                                {healthy ? (
                                                    <CheckCircle className="text-success" size={16} />
                                                ) : (
                                                    <AlertTriangle className="text-warning" size={16} />
                                                )}
                                                <span
                                                    className={`${healthy ? "text-success" : "text-warning"} fw-semibold`}
                                                    style={{ fontSize: "0.9rem" }}
                                                >
                                                    {item.description}
                                                </span>

                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            <tr className="bg-primary bg-opacity-10" style={{ borderTop: '2px solid rgba(52, 143, 226, 0.2)' }}>
                                <td className="px-4 py-3 fw-bold text-dark">TOTAL</td>
                                <td className="px-4 py-3 text-end fw-bold text-primary fs-5">{totalLeads.toLocaleString()}</td>
                                <td className="px-4 py-3 text-end fw-bold text-dark"> {totalPercentage}%</td>
                                <td className="px-4 py-3"></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

const RedFlagTable = ({ dispositionData, loading }) => {
    const redFlagChecks = dispositionData?.red_flag_checks || [];

    if (loading) {
        return (
            <div className="panel panel-default with-rounded-corner with-shadow mb-5">
                <div className="panel-body p-5 text-center">
                    <div className="spinner-border text-danger" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Loading red flag metrics...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="panel panel-default with-rounded-corner with-shadow mb-5">
            <div className="panel-heading">
                <div className="d-flex align-items-center gap-2">
                    <AlertTriangle className="text-danger" size={18} />
                    <h4 className="mb-0 fw-bold text-dark" style={{ fontSize: '1.1rem' }}>Red Flag Checks</h4>
                </div>
            </div>
            <div className="panel-body p-0">
                <div className="table-responsive">
                    <table className="table table-hover mb-0">
                        <thead>
                            <tr className="bg-light bg-opacity-50">
                                <th className="px-4 py-3 border-0 text-muted small fw-bold text-uppercase">Metric</th>
                                <th className="px-4 py-3 border-0 text-muted small fw-bold text-uppercase text-center">Current</th>
                                <th className="px-4 py-3 border-0 text-muted small fw-bold text-uppercase text-center">Warning Level</th>
                                <th className="px-4 py-3 border-0 text-muted small fw-bold text-uppercase text-center">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {redFlagChecks.map((item, index) => (
                                <tr key={item.metric} className="align-middle border-bottom border-light">
                                    <td className="px-4 py-3">
                                        <span className="fw-semibold text-dark">{item.metric}</span>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <span className="fw-bold text-primary">{item.current}%</span>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        {(() => {
                                            const severity = (item.severity || "").toUpperCase();
                                            const isRed = severity === "RED";

                                            return (
                                                <span
                                                    className={`${isRed ? "text-danger" : "text-warning"} fw-semibold text-uppercase`}
                                                    style={{ fontSize: "0.9rem" }}
                                                >
                                                    {item.warning_level ?? "--"}
                                                </span>
                                            );
                                        })()}
                                    </td>

                                    <td className="px-4 py-3 text-center">
                                        <div className="d-flex align-items-center justify-content-center gap-2">
                                            {item.is_healthy ? (
                                                <>
                                                    <CheckCircle className="text-success" size={16} />
                                                    <span
                                                        className="text-success fw-semibold text-uppercase"
                                                        style={{ fontSize: "0.9rem" }}
                                                    >
                                                        Healthy
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <AlertTriangle className="text-danger" size={16} />
                                                    <span
                                                        className="text-danger fw-semibold text-uppercase"
                                                        style={{ fontSize: "0.9rem" }}
                                                    >
                                                        Unhealthy
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    </td>

                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

const ConversionFlowTable = ({ startDate, endDate }) => {
    const [flowData, setFlowData] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchFlow = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("authToken");
            const url = `${API_URL}executive-report/conversion-flow?startDate=${startDate.format("YYYY-MM-DD")}&endDate=${endDate.format("YYYY-MM-DD")}`;
            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}`, Accept: "*/*" },
            });

            if (response.data) {
                // Handle both standard success wrapper and direct response
                if (response.data.success) {
                    setFlowData(response.data.data.data || []);
                } else {
                    setFlowData(response.data.data || []);
                }
            }
        } catch (err) {
            console.error("Failed to fetch conversion flow", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFlow();
    }, [startDate, endDate]);

    if (loading) {
        return (
            <div className="panel panel-default with-rounded-corner with-shadow mb-5">
                <div className="panel-body p-5 text-center">
                    <div className="spinner-border text-info" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Loading conversion flow...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="panel panel-default with-rounded-corner with-shadow mb-5">
            <div className="panel-heading">
                <div className="d-flex align-items-center gap-2">
                    <Filter className="text-info" size={18} />
                    <h4 className="mb-0 fw-bold text-dark" style={{ fontSize: '1.1rem' }}>SALES FUNNEL - LEAD CONVERSION FLOW</h4>
                </div>
            </div>
            <div className="panel-body p-0">
                <div className="table-responsive">
                    <table className="table table-hover mb-0">
                        <thead>
                            <tr className="bg-light bg-opacity-50">
                                <th className="px-4 py-3 border-0 text-muted small fw-bold text-uppercase">Stage</th>
                                <th className="px-4 py-3 border-0 text-muted small fw-bold text-uppercase text-end">Lead Count</th>
                                <th className="px-4 py-3 border-0 text-muted small fw-bold text-uppercase text-end">% of Previous Stage</th>
                                <th className="px-4 py-3 border-0 text-muted small fw-bold text-uppercase text-end">Avg Conv. Time</th>
                            </tr>
                        </thead>
                        <tbody>
                            {flowData.length > 0 ? (
                                flowData.map((item, index) => (
                                    <tr key={index} className="align-middle border-bottom border-light">
                                        <td className="px-4 py-3">
                                            <span className="fw-semibold text-dark">{item.stage}</span>
                                        </td>
                                        <td className="px-4 py-3 text-end fw-bold text-dark">
                                            {item.leadCount?.toLocaleString()}
                                        </td>
                                        <td className="px-4 py-3 text-end">
                                            <span className="badge bg-light text-primary border">{item.percentOfPreviousStage}</span>
                                        </td>
                                        <td className="px-4 py-3 text-end text-muted">
                                            {item.avgConvTime}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="text-center py-4 text-muted">No conversion data available for this period</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

const DropOffAnalysisTable = ({ startDate, endDate }) => {
    const [funnelData, setFunnelData] = useState(null);
    const [loading, setLoading] = useState(false);

    const fetchDropOff = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("authToken");
            const url = `${API_URL}executive-report/drop-off-analysis?startDate=${startDate.format("YYYY-MM-DD")}&endDate=${endDate.format("YYYY-MM-DD")}`;
            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}`, Accept: "*/*" },
            });

            if (response.data) {
                setFunnelData(response.data);
            }
        } catch (err) {
            console.error("Failed to fetch drop-off analysis", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDropOff();
    }, [startDate, endDate]);

    if (loading) {
        return (
            <div className="panel panel-default with-rounded-corner with-shadow mb-5">
                <div className="panel-body p-5 text-center">
                    <div className="spinner-border text-danger" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Loading funnel analysis...</p>
                </div>
            </div>
        );
    }

    if (!funnelData || !funnelData.data) {
        return (
            <div className="panel panel-default with-rounded-corner with-shadow mb-5">
                <div className="panel-body p-5 text-center">
                    <p className="text-muted">No funnel data available for this period</p>
                </div>
            </div>
        );
    }

    const data = funnelData.data;
    const dateRange = funnelData.dateRange;

    // Calculate total of the 6 fields
    const totalCount = (data.New || 0) + (data.FollowUp || 0) + (data.NoResponse || 0) +
        (data.NotInterested || 0) + (data.AlternativeNumber || 0) + (data.Junk || 0);

    // Prepare funnel data points for 3 stages: Total, Prospect, Subscribed
    const funnelDataPoints = [
        { label: "Total", y: totalCount, color: "#3f51b5" },
        { label: "Prospect", y: data.Prospect || 0, color: "#ff9800" },
        { label: "Subscribed", y: data.Subscribed || 0, color: "#00bfa5" }
    ];

    const funnelOptions = {
        animationEnabled: true,
        title: { text: "" },
        data: [
            {
                type: "funnel",
                indexLabel: "{label} - {y}",
                toolTipContent: "<b>{label}</b>: {y}",
                valueRepresents: "area",
                dataPoints: funnelDataPoints,
            },
        ],
    };

    return (
        <div className="panel panel-default with-rounded-corner with-shadow mb-5">
            <div className="panel-heading">
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                        <Filter className="text-info" size={18} />
                        <h4 className="mb-0 fw-bold text-dark" style={{ fontSize: '1.1rem' }}>{funnelData.title || "FUNNEL SUMMARY"}</h4>
                    </div>
                    {dateRange && (
                        <div className="badge bg-light text-dark border px-3 py-2 rounded-pill">
                            {dateRange.startDate} to {dateRange.endDate}
                        </div>
                    )}
                </div>
            </div>
            <div className="panel-body p-4">
                <div className="row">
                    <div className="col-12 text-center mb-3">
                        <h5 className="fw-bold text-dark">Total: {totalCount.toLocaleString()}</h5>
                    </div>
                    <div className="col-12">
                        <div style={{ height: "370px", maxWidth: "480px", margin: "0px auto" }}>
                            <CanvasJSChart options={funnelOptions} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const LeadDispositionChart = ({ dispositionData, loading }) => {
    const [selectedStatus, setSelectedStatus] = useState(null);

    const transformTrendData = (series) => {
        if (!series || series.length === 0) return [];
        const dateMap = {};
        series.forEach(s => {
            (s.data || []).forEach(d => {
                const dateKey = moment(d.date).format("YYYY-MM-DD");
                if (!dateMap[dateKey]) {
                    dateMap[dateKey] = {
                        date: dateKey,
                        displayDate: moment(d.date).format("MMM DD"),
                    };
                }
                dateMap[dateKey][s.status] = d.count;
            });
        });
        return Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));
    };

    const series = dispositionData?.series || [];
    const trendData = transformTrendData(series);
    const totalLeads = dispositionData?.total_leads || 0;

    if (loading) {
        return (
            <div className="panel panel-default with-rounded-corner with-shadow mb-5">
                <div className="panel-body p-5 text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Loading trend analysis...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="panel panel-default with-rounded-corner with-shadow mb-5">
            <div className="panel-body p-15">
                {/* Header & Title - Matching PerformanceComparisonChart */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "15px 0" }}>
                    <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "500", color: "#333" }}>
                        Lead Disposition Trend
                    </h3>
                    <div className="badge bg-light text-dark border px-3 py-2 rounded-pill fw-bold">
                        Total Leads: {totalLeads.toLocaleString()}
                    </div>
                </div>

                {/* Status Summary - Matching PerformanceComparisonChart legend style */}
                <div style={{ marginBottom: "20px" }}>
                    <div style={{ fontSize: "14px", color: "#666", marginBottom: "12px" }}>
                        Lead status performance overview for the selected period
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
                        {series.map((s, idx) => {
                            const total = (s.data || []).reduce((acc, d) => acc + d.count, 0);
                            const isSelected = selectedStatus === s.status;
                            return (
                                <div
                                    key={s.status}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        cursor: "pointer",
                                        opacity: selectedStatus && !isSelected ? 0.4 : 1,
                                        transition: "all 0.2s"
                                    }}
                                    onClick={() => setSelectedStatus(isSelected ? null : s.status)}
                                >
                                    <div style={{
                                        width: "12px",
                                        height: "12px",
                                        borderRadius: "50%",
                                        backgroundColor: s.color || LEAD_COLORS[idx % LEAD_COLORS.length]
                                    }}></div>
                                    <span style={{ fontSize: "14px", color: "#666" }}>{s.status}</span>
                                    <span style={{ fontWeight: "600", color: "#333" }}>{total.toLocaleString()}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Chart Section - Matching PerformanceComparisonChart layout */}
                <div style={{ width: "100%", height: "350px", marginBottom: "20px" }}>
                    <ResponsiveContainer>
                        <LineChart data={trendData} margin={{ top: 20, right: 20, bottom: 20, left: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                            <XAxis
                                dataKey="displayDate"
                                tick={{ fontSize: 12, fill: "#666" }}
                                tickLine={false}
                                axisLine={false}
                                dy={10}
                            />
                            <YAxis
                                tick={{ fontSize: 12, fill: "#666" }}
                                tickLine={false}
                                axisLine={false}
                                tickFormatter={(val) => val.toLocaleString()}
                            />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: "white",
                                    border: "1px solid #ddd",
                                    borderRadius: "8px",
                                    fontSize: "12px",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                }}
                            />
                            {series.map((s, idx) => (
                                <Line
                                    key={s.status}
                                    type="monotone"
                                    dataKey={s.status}
                                    name={s.status}
                                    stroke={s.color || LEAD_COLORS[idx % LEAD_COLORS.length]}
                                    strokeWidth={selectedStatus === s.status ? 3.5 : 2}
                                    dot={false}
                                    activeDot={{ r: 6, strokeWidth: 0 }}
                                    hide={selectedStatus && selectedStatus !== s.status}
                                    animationDuration={1500}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                {/* Footer Note - Matching PerformanceComparisonChart style */}
                <div style={{ padding: "15px 0", borderTop: "1px solid #f0f0f0" }}>
                    <p style={{ fontSize: "12px", color: "#888", margin: 0, lineHeight: "1.4" }}>
                        <span style={{ fontWeight: "500" }}>Note:</span> Lead disposition trends reflect the real-time engagement and quality metrics
                        across all active campaigns. Use this to monitor conversion health and identify performance bottlenecks.
                    </p>
                </div>
            </div>
        </div>
    );
};



const AnalysisDashboard = () => {
    const [dispositionData, setDispositionData] = useState(null);
    const [loading, setLoading] = useState(true);

    // Set default filter to last 30 days
    const [dateRange, setDateRange] = useState({
        startDate: moment().subtract(29, "days"),
        endDate: moment(),
    });
    const { startDate, endDate } = dateRange;

    const fetchDisposition = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("authToken");
            // Use the provided devtunnel API with new-disposition endpoint
            const url = `${API_URL}executive-report/new-disposition?startDate=${startDate.format("YYYY-MM-DD")}&endDate=${endDate.format("YYYY-MM-DD")}`;

            const response = await axios.get(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: "*/*"
                },
            });

            if (response.data && response.data.success) {
                const rawData = response.data.data;

                // Transform series data into summary format for tables
                const dispositionSummary = (rawData.series || []).map(s => {
                    const total = (s.data || []).reduce((acc, d) => acc + d.count, 0);
                    return {
                        status: s.status,
                        count: total,
                        description: s.description,
                        color: s.color,
                        icon: s.icon,
                        percentage: rawData.total_leads > 0
                            ? ((total / rawData.total_leads) * 100).toFixed(1)
                            : 0
                    };
                });

                setDispositionData({
                    ...rawData,
                    disposition: dispositionSummary,
                    // Keep red_flag_checks if they existing in the data, or initialize if missing
                    red_flag_checks: rawData.red_flag_checks || []
                });
            }
        } catch (err) {
            console.error("Failed to fetch disposition", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDisposition();
    }, [startDate, endDate]);

    const handleApply = (event, picker) => {
        setDateRange({
            startDate: picker.startDate,
            endDate: picker.endDate,
        });
    };

    const ranges = {
        "Today": [moment(), moment()],
        "Yesterday": [moment().subtract(1, "days"), moment().subtract(1, "days")],
        "Last 7 Days": [moment().subtract(6, "days"), moment()],
        "Last 30 Days": [moment().subtract(29, "days"), moment()],
        "This Month": [moment().startOf("month"), moment().endOf("month")],
        "Last Month": [
            moment().subtract(1, "month").startOf("month"),
            moment().subtract(1, "month").endOf("month"),
        ],
    };

    return (
        <div className="page-header-fixed page-sidebar-fixed fade in mt-5">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h1 className="page-header mb-0">
                        Analysis Dashboard <small>Lead Disposition Insights</small>
                    </h1>
                </div>

                <div className="d-flex gap-2">
                    <DateRangePicker
                        initialSettings={{
                            ranges: ranges,
                            startDate: startDate,
                            endDate: endDate,
                            locale: { format: "DD-MM-YYYY" },
                        }}
                        onApply={handleApply}
                    >
                        <div className="input-group" style={{ cursor: 'pointer', width: '240px' }}>
                            <input
                                type="text"
                                className="form-control"
                                value={`${startDate.format("DD-MM-YYYY")} - ${endDate.format("DD-MM-YYYY")}`}
                                readOnly
                                style={{ background: '#fff', cursor: 'pointer' }}
                            />
                            <span className="input-group-text bg-light">
                                <FontAwesomeIcon icon={faCalendarAlt} />
                            </span>
                        </div>
                    </DateRangePicker>
                </div>
            </div>

            <div className="row">
                <div className="col-12">
                    <LeadDispositionChart dispositionData={dispositionData} loading={loading} />
                </div>
            </div>

            <div className="row">
                <div className="col-12">
                    <LeadStatusTable dispositionData={dispositionData} loading={loading} />
                </div>
            </div>

            <div className="row">
                <div className="col-12">
                    <RedFlagTable dispositionData={dispositionData} loading={loading} />
                </div>
            </div>

            <div className="row">
                <div className="col-12">
                    <LeadSourceTable startDate={startDate} endDate={endDate} loading={loading} />
                </div>
            </div>

            <div className="row">
                <div className="col-12">
                    <ConversionFlowTable startDate={startDate} endDate={endDate} />
                </div>
            </div>

            <div className="row">
                <div className="col-12">
                    <DropOffAnalysisTable startDate={startDate} endDate={endDate} />
                </div>
            </div>


        </div>
    );
};

export default AnalysisDashboard;
